// Background file exports for the current user: the Downloads list in the
// profile menu, the "ready" toast and the badge dot. Jobs come from
// GET /api/exports/ (last 24h, this tenant) and are re-checked every few
// seconds while any is still preparing. Nothing survives a page reload except
// what the server lists, which is fine: the list is the source of truth.
import { getExportDownload, listExports } from '@/services/api/exportsApi'
import { dismissToast, notifyLoadError, showErrorToast, showSuccessToast } from '@/utils/loadError'

export const POLL_INTERVAL_MS = 3000
const ACTIVE = new Set(['pending', 'in_progress'])

let pollTimer = null

const readyToastId = (jobId) => `export-ready:${jobId}`

function stopTimer() {
  if (pollTimer) clearTimeout(pollTimer)
  pollTimer = null
}

export default {
  namespaced: true,
  state: () => ({
    jobs: [],
    // Ready exports finished since the user last opened the profile menu.
    unseenReadyIds: [],
  }),
  getters: {
    jobs: (state) => state.jobs,
    job: (state) => (id) => state.jobs.find((j) => j.id === id) || null,
    hasActive: (state) => state.jobs.some((j) => ACTIVE.has(j.status)),
    hasUnseenReady: (state) => state.unseenReadyIds.length > 0,
  },
  mutations: {
    SET_JOBS(state, jobs) {
      state.jobs = jobs
      const ids = new Set(jobs.map((j) => j.id))
      state.unseenReadyIds = state.unseenReadyIds.filter((id) => ids.has(id))
    },
    UPSERT_JOB(state, job) {
      const i = state.jobs.findIndex((j) => j.id === job.id)
      if (i === -1) state.jobs.unshift(job)
      else state.jobs.splice(i, 1, job)
    },
    ADD_UNSEEN_READY(state, id) {
      if (!state.unseenReadyIds.includes(id)) state.unseenReadyIds.push(id)
    },
    CLEAR_UNSEEN(state) {
      state.unseenReadyIds = []
    },
  },
  actions: {
    // Initial load (and on tenant switch): no toasts for jobs that finished earlier.
    async load({ commit, dispatch }) {
      stopTimer()
      try {
        commit('SET_JOBS', await listExports())
      } catch (error) {
        notifyLoadError(error, { feature: 'exports', endpoint: '/api/exports/', message: "Couldn't load your downloads." })
        return
      }
      dispatch('schedulePoll')
    },
    // Called with the job an export endpoint just returned.
    track({ commit, dispatch }, job) {
      commit('UPSERT_JOB', job)
      dispatch('schedulePoll')
    },
    schedulePoll({ getters, dispatch }) {
      stopTimer()
      if (getters.hasActive) pollTimer = setTimeout(() => dispatch('poll'), POLL_INTERVAL_MS)
    },
    async poll({ state, commit, dispatch }) {
      pollTimer = null
      const before = new Map(state.jobs.map((j) => [j.id, j.status]))
      let jobs
      try {
        jobs = await listExports()
      } catch {
        // A blip shouldn't drop the user's pending exports; try again next tick.
        dispatch('schedulePoll')
        return
      }
      commit('SET_JOBS', jobs)
      for (const job of jobs) {
        if (!ACTIVE.has(before.get(job.id))) continue
        if (job.status === 'completed') {
          commit('ADD_UNSEEN_READY', job.id)
          showSuccessToast(`Your ${job.label} export is ready. Click to download.`, {
            toastId: readyToastId(job.id),
            timeout: false,
            onClick: () => dispatch('download', job),
          })
        } else if (job.status === 'failed') {
          showErrorToast(job.error_message || `Your ${job.label} export failed.`)
        }
      }
      dispatch('schedulePoll')
    },
    async download({ dispatch }, job) {
      dispatch('markSeen')
      try {
        const { url } = await getExportDownload(job.id)
        // The presigned URL answers with Content-Disposition: attachment, so the
        // browser downloads the file and stays on the page.
        window.location.assign(url)
      } catch (error) {
        notifyLoadError(error, {
          feature: 'exports',
          endpoint: '/api/exports/{id}/download/',
          message: "Couldn't download this export. It may have expired.",
        })
      }
    },
    // Close sticky "ready" toasts (e.g. when the profile menu opens over them).
    dismissReadyToasts({ state }) {
      state.unseenReadyIds.forEach((id) => dismissToast(readyToastId(id)))
    },
    // Opening the Downloads page, or downloading, acknowledges ready exports.
    markSeen({ commit, dispatch }) {
      dispatch('dismissReadyToasts')
      commit('CLEAR_UNSEEN')
    },
    reset({ commit }) {
      stopTimer()
      commit('SET_JOBS', [])
    },
  },
}
